import React, { useState } from 'react';
import { Layout, Typography, Row, Col, Card, Divider, Space, Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Ambassador, AmbassadorMetadata } from '../../types';
import AmbassadorMap from '../map/AmbassadorMap';
import KPITiles from '../metrics/KPITiles';
import CardGrid from '../cards/CardGrid';
import FilterPanel from '../filters/FilterPanel';
import CountryFilter from '../filters/CountryFilter';
import AmbassadorModal from '../modals/AmbassadorModal';
import AnalyticsCharts from '../charts/AnalyticsCharts';
import { useFilteredAmbassadors, useFilterStore } from '../../store/useFilters';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;

interface DashboardLayoutProps {
  ambassadors: Ambassador[];
  metadata: AmbassadorMetadata | null;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  ambassadors,
  metadata,
}) => {
  const [selectedAmbassador, setSelectedAmbassador] = useState<Ambassador | null>(null);
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalAmbassador, setModalAmbassador] = useState<Ambassador | null>(null);
  
  // Use optimized selector for filtered ambassadors
  const filteredAmbassadors = useFilteredAmbassadors(ambassadors);

  const handleAmbassadorSelect = (ambassador: Ambassador | null) => {
    setSelectedAmbassador(ambassador);
    if (ambassador) {
      setModalAmbassador(ambassador);
      setModalVisible(true);
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setModalAmbassador(null);
    setSelectedAmbassador(null);
  };

  return (
    <Layout style={{ minHeight: '100vh', height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Header 
        className="dashboard-header"
        style={{ 
          background: 'white', 
          borderBottom: '1px solid #f0f0f0',
          boxShadow: '0 2px 8px rgba(0, 93, 131, 0.06)'
        }}
      >
        <Row align="middle" justify="space-between" style={{ height: '100%' }}>
          <Col>
            <Space align="center" size="middle">
              <img
                src="/trace_blue.png"
                alt="Artelia TRACE logo"
                className="header-logo"
              />
              <div>
                <Title level={4} style={{ margin: 0, color: '#005d83' }}>
                  Ambassador Network
                </Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Artelia Eco-Design Dashboard
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <div style={{ textAlign: 'right' }}>
              <Text strong style={{ color: '#005d83', fontSize: '16px' }}>
                {filteredAmbassadors.length} of {ambassadors.length} ambassadors
              </Text>
              {metadata && (
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Last updated: {new Date(metadata.lastUpdated).toLocaleDateString()}
                  </Text>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Header>

      <Layout>
        {/* Filter Sidebar */}
        <Sider
          collapsed={siderCollapsed}
          onCollapse={setSiderCollapsed}
          width={320}
          style={{
            background: 'white',
            borderRight: '1px solid #f0f0f0',
            position: 'relative',
          }}
          trigger={null}
        >
          {/* Custom chevron toggle button */}
          <Button
            type="text"
            icon={siderCollapsed ? <RightOutlined /> : <LeftOutlined />}
            onClick={() => setSiderCollapsed(!siderCollapsed)}
            style={{
              position: 'absolute',
              right: '-15px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1000,
              background: 'white',
              border: '1px solid #f0f0f0',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          />
          <div style={{ padding: '16px', height: '100%' }}>
            <FilterPanel ambassadors={ambassadors} />
          </div>
        </Sider>

        {/* Main Content */}
        <Layout style={{ padding: '24px', overflow: 'visible' }}>
          <Content style={{ overflow: 'visible', minHeight: '600px' }}>
            {/* KPI Section */}
            <KPITiles 
              ambassadors={ambassadors}
              filteredAmbassadors={filteredAmbassadors}
            />

            <Divider style={{ margin: '24px 0' }} />

            {/* Main Dashboard Section */}
            <Row gutter={[24, 24]} style={{ minHeight: '800px' }}>
              
              {/* Left Column: Map and Analytics */}
              <Col xs={24} lg={16} style={{ minHeight: '800px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '24px', minHeight: 0 }}>
                  
                  {/* Interactive Map */}
                  <Card 
                    title={
                      <Row justify="space-between" align="middle">
                        <Col>
                          <span>Geographic Distribution</span>
                        </Col>
                        <Col>
                          <div style={{ width: '200px' }}>
                            <CountryFilter ambassadors={ambassadors} />
                          </div>
                        </Col>
                      </Row>
                    }
                    style={{ height: '400px', flexShrink: 0 }}
                    styles={{ body: { padding: 0, height: 'calc(100% - 57px)' } }}
                  >
                    <AmbassadorMap 
                      ambassadors={filteredAmbassadors}
                      selectedAmbassador={selectedAmbassador}
                      onAmbassadorSelect={handleAmbassadorSelect}
                    />
                  </Card>

                  {/* Interactive Analytics Charts */}
                  <Card 
                    style={{ flex: 1, minHeight: 0 }}
                    styles={{ body: { padding: '16px', height: '100%', overflow: 'visible' } }}
                  >
                    <AnalyticsCharts 
                      ambassadors={ambassadors}
                      filteredAmbassadors={filteredAmbassadors}
                    />
                  </Card>
                </div>
              </Col>

              {/* Right Column: Ambassador Directory - Full Height */}
              <Col xs={24} lg={8} style={{ minHeight: '1000px' }}>
                <Card 
                  title="Ambassador Directory"
                  style={{ minHeight: '1000px' }}
                  styles={{ body: { padding: '16px', overflow: 'hidden' } }}
                >
                  <CardGrid 
                    ambassadors={filteredAmbassadors}
                    selectedAmbassador={selectedAmbassador}
                    onAmbassadorSelect={handleAmbassadorSelect}
                  />
                </Card>
              </Col>
            </Row>
          </Content>
        </Layout>
      </Layout>

      {/* Ambassador Detail Modal */}
      <AmbassadorModal
        ambassador={modalAmbassador}
        visible={modalVisible}
        onClose={handleModalClose}
      />
    </Layout>
  );
};

export default DashboardLayout;